<template>
  <div class="will-collection-booking-form-container">
    <v-card class="mb-4 pa-4" color="#43a047" dark>
      <div class="text-h6 font-weight-regular">
        <template v-if="bookingMode === 'advisor'">
          Schedule one or multiple <strong>validated will</strong> collections
          with the below information
        </template>
        <template v-else>
          Please provide us with the below information to schedule the
          collection of your will.
        </template>
      </div>
    </v-card>
    <p class="text-danger">* Indicates required question</p>
    <ErrorSnackbar v-model="showError" :error="errorMessage" />

    <!--
      Adviser bookings: one courier collects several wills. The adviser ticks
      the wills they physically have in their possession and are handing to
      this courier. Nothing is ticked to start with - possession is the
      adviser's call, not the system's.
    -->
    <v-card v-if="bookingMode === 'advisor'" class="mb-4 pa-4">
      <p class="mb-1">
        <strong>Wills to collect *</strong>
      </p>
      <div v-if="$apollo.queries.eligibleTestators.loading" class="py-2">
        <v-progress-circular indeterminate size="20" color="primary" />
        <span class="pl-3">Loading the wills you hold...</span>
      </div>
      <template v-else-if="eligibleTestators.length">
        <p class="text-body-2 mb-2">
          Tick the validated wills you have in your possession and will hand to
          this courier.
        </p>
        <div class="testator-picker">
          <Checkbox
            v-for="eligible in eligibleTestators"
            :key="eligible.id"
            :value="isTestatorSelected(eligible.id)"
            :label="eligible.fullName || `Testator ${eligible.id}`"
            color="primary"
            @input="toggleTestator(eligible.id, $event)"
          />
        </div>
        <p v-if="!selectedTestatorIds.length" class="text-danger mb-0">
          Select at least one will you have in your possession.
        </p>
      </template>
      <v-alert v-else type="info" text dense class="mb-0">
        None of the wills you hold are ready for collection: they must be
        validated, not yet vaulted, and not already booked.
      </v-alert>
    </v-card>

    <DryRunRequestsDialog
      v-model="showDryRunPreview"
      :requests="dryRunRequests"
      @input="onDryRunPreviewInput"
    />

    <ExistingCollectionBookingDialog
      v-model="showDuplicateDialog"
      :booking="duplicateBooking"
      :mode="bookingMode"
      :booking-testator-id="testator.id"
      :testator-name="testator.fullName"
      :preselected-testator-ids="selectedTestatorIds"
      @linked="onLinkedToExistingBooking"
    />

    <ValidationObserver ref="observer">
      <v-form @submit.prevent="submitForm">
        <v-card class="mb-4 pa-4">
          <v-row>
            <v-col cols="12">
              <ValidationProvider
                id="email"
                v-slot="{ errors }"
                name="email"
                rules="required|email"
              >
                <TextField
                  v-model.trim="form.email"
                  label="Email Address *"
                  :errors="errors"
                  :name="'email'"
                />
              </ValidationProvider>
            </v-col>
            <v-col cols="12">
              <ValidationProvider
                id="full_name"
                v-slot="{ errors }"
                name="full_name"
                rules="required"
              >
                <TextField
                  v-model.trim="form.fullName"
                  :label="'First & Last Name *'"
                  :name="'full_name'"
                  :errors="errors"
                />
              </ValidationProvider>
            </v-col>
            <v-col cols="12">
              <ValidationProvider
                id="cell_number"
                v-slot="{ errors }"
                name="cell_number"
                :rules="{ required: true, phoneIntl: [form.cellCountryCode] }"
              >
                <TextField
                  v-model.trim="form.cellNumber"
                  :label="'Cell number*'"
                  :errors="errors"
                  :name="'cell_number'"
                  :isCountryCode="true"
                >
                  <template #prepend>
                    <vue-tel-input
                      :preferred-countries="['ZA', 'GB']"
                      :default-country="form.cellCountryCode"
                      @country-changed="countrySelected"
                    >
                      <template #arrow-icon>
                        <strong>{{ countryDialCode }}</strong>
                      </template>
                    </vue-tel-input>
                  </template>
                </TextField>
              </ValidationProvider>
            </v-col>
            <v-col cols="12">
              <ValidationProvider
                id="alternative_cell_number"
                v-slot="{ errors }"
                name="alternative_cell_number"
                :rules="{ phoneIntl: [form.altCellCountryCode] }"
              >
                <TextField
                  v-model.trim="form.alternativeCellNumber"
                  :label="'Alternative number for someone who will always be at the place of collection'"
                  :name="'alternative_cell_number'"
                  :errors="errors"
                  :isCountryCode="true"
                >
                  <template #prepend>
                    <vue-tel-input
                      :preferred-countries="['ZA', 'GB']"
                      :default-country="form.altCellCountryCode"
                      @country-changed="altCountrySelected"
                    >
                      <template #arrow-icon>
                        <strong>{{ altCountryDialCode }}</strong>
                      </template>
                    </vue-tel-input>
                  </template>
                </TextField>
              </ValidationProvider>
            </v-col>
            <v-col cols="12">
              <ValidationProvider
                id="google_address"
                v-slot="{ errors }"
                name="google_address"
              >
                <GoogleMapsAddressPicker
                  v-model="addressDetails"
                  @address-selected="onAddressSelected"
                />
              </ValidationProvider>
            </v-col>
            <v-col cols="12">
              <ValidationProvider
                id="special_instructions_collection"
                v-slot="{ errors }"
                name="special_instructions_collection"
              >
                <TextField
                  v-model.trim="form.specialInstructionsCollection"
                  :label="'Complex/Company/Building Details/Special Instructions (optional)'"
                  :name="'special_instructions_collection'"
                  :errors="errors"
                />
              </ValidationProvider>
            </v-col>
            <v-col xl="3" cols="12">
              <ValidationProvider
                id="street_number"
                v-slot="{ errors }"
                name="street_number"
                rules="required"
              >
                <TextField
                  v-model.trim="form.streetNumber"
                  :label="'Street number *'"
                  :name="'street_number'"
                  :errors="errors"
                />
              </ValidationProvider>
            </v-col>
            <v-col xl="9" cols="12">
              <ValidationProvider
                id="street_name"
                v-slot="{ errors }"
                name="street_name"
                rules="required"
              >
                <TextField
                  v-model.trim="form.streetName"
                  :label="'Street Name *'"
                  :name="'street_name'"
                  :errors="errors"
                />
              </ValidationProvider>
            </v-col>
            <v-col cols="12" xl="6" lg="6">
              <ValidationProvider
                id="suburb"
                v-slot="{ errors }"
                name="suburb"
                rules="required"
              >
                <TextField
                  v-model.trim="form.suburb"
                  :label="'Suburb *'"
                  :name="'suburb'"
                  :errors="errors"
                />
              </ValidationProvider>
            </v-col>
            <v-col cols="12" xl="6" lg="6">
              <ValidationProvider
                id="city"
                v-slot="{ errors }"
                name="city"
                rules="required"
              >
                <TextField
                  v-model.trim="form.city"
                  :label="'City *'"
                  :name="'city'"
                  :errors="errors"
                />
              </ValidationProvider>
            </v-col>
            <v-col cols="12" xl="6" lg="6">
              <ValidationProvider
                id="province"
                v-slot="{ errors }"
                name="province"
                rules="required"
              >
                <ListSelect
                  v-model="form.province"
                  :items="provinces"
                  :group="'province'"
                  :name="'province'"
                  :label="'Province *'"
                  :errors="errors"
                />
              </ValidationProvider>
            </v-col>
            <v-col cols="12" xl="6" lg="6">
              <ValidationProvider
                id="postal_code"
                v-slot="{ errors }"
                name="postal_code"
              >
                <TextField
                  v-model.trim="form.postalCode"
                  :label="'Post code'"
                  :name="'post-code'"
                  :errors="errors"
                />
              </ValidationProvider>
            </v-col>
            <v-col v-if="duplicateBookingAtCurrentAddress" cols="12">
              <v-alert type="warning" text dense class="mb-0">
                A collection is already booked for this address
                <template v-if="duplicateBookingWhen">
                  on {{ duplicateBookingWhen }}</template
                >. A second booking cannot be made here; submit to add the wills
                to that collection instead, or change the address.
              </v-alert>
            </v-col>
            <v-col cols="12" class="py-0">
              <div
                class="map-wrapper"
                :class="{ 'map-visible': showMapPreview }"
              >
                <div class="d-flex align-center mb-5">
                  <v-icon>{{ icons.mdiInformation }}</v-icon>
                  <span class="pl-3">
                    We have placed a pin on the map based on the address that
                    you have provided. If the pin isn’t exactly where the
                    courier should collect from, simply drag it to the correct
                    location.
                  </span>
                </div>
                <div ref="mapElement" class="map-element"></div>
              </div>
            </v-col>
            <v-col cols="12">
              <v-menu
                v-model="dateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template #activator="{ on }">
                  <ValidationProvider
                    id="collection_date"
                    v-slot="{ errors }"
                    name="collection_date"
                    rules="required"
                  >
                    <TextField
                      v-model="form.collectionDate"
                      :label="'Preferred collection date *'"
                      :name="'collection_date'"
                      :isDateField="true"
                      :readonly="true"
                      :errors="errors"
                      v-on="on"
                    />
                  </ValidationProvider>
                </template>
                <v-date-picker
                  v-model="form.collectionDate"
                  :min="minCollectionDate"
                  @input="dateMenu = false"
                />
              </v-menu>
            </v-col>
            <v-col cols="12">
              <label
                >Accept
                <a
                  href="https://thecourierguy.co.za/terms-conditions/"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="terms-link"
                  >Terms & Conditions</a
                >
                <span class="text-danger">*</span></label
              >
              <ValidationProvider
                id="terms_and_conditions"
                v-slot="{ errors }"
                name="terms_and_conditions"
                rules="required"
              >
                <RadioSelect
                  v-model="form.termsAndConditions"
                  :items="[{ text: 'Yes', value: true }]"
                  :errors="errors"
                  :group="'terms_and_conditions'"
                  :name="'terms_and_conditions'"
                />
              </ValidationProvider>
            </v-col>
          </v-row>
        </v-card>
        <div class="d-flex justify-end mt-10 mb-10">
          <GradientButton
            :title="'Submit Booking'"
            :small="true"
            :loading="loading"
            :type="'submit'"
          />
        </div>
      </v-form>
    </ValidationObserver>
  </div>
</template>

<script>
  import ErrorSnackbar from '../ErrorSnackbar.vue';
  import DryRunRequestsDialog from './DryRunRequestsDialog.vue';
  import ExistingCollectionBookingDialog from './ExistingCollectionBookingDialog.vue';
  import GoogleMapsAddressPicker from '../GoogleMapsAddressPicker.vue';
  import {
    CREATE_TESTATOR_WILL_COLLECTION_BOOKING_MUTATION,
    CREATE_ADVISOR_WILL_COLLECTION_BOOKINGS_MUTATION,
    PENDING_WILL_COLLECTION_BOOKING_AT_ADDRESS_QUERY,
    ADVISOR_COLLECTION_ELIGIBLE_TESTATORS_QUERY,
  } from '../../../constants/graphql/testators';
  import { parsePhoneNumber } from 'awesome-phonenumber';
  import { mdiInformation } from '@mdi/js';
  export default {
    name: 'WillCollectionBookingForm',
    components: {
      ErrorSnackbar,
      GoogleMapsAddressPicker,
      DryRunRequestsDialog,
      ExistingCollectionBookingDialog,
    },
    props: {
      testator: {
        type: Object,
        required: true,
      },
      // When the form is hosted inside a dialog, the host shows the dry-run
      // preview itself after closing, so two overlays never stack.
      showPreviewInline: {
        type: Boolean,
        default: true,
      },
      // Same idea for the "collection already booked here" dialog: a dialog
      // host passes false and listens for `existing-collection` instead, since
      // a v-dialog nested inside another v-dialog cannot be interacted with.
      showExistingCollectionInline: {
        type: Boolean,
        default: true,
      },
      // 'testator' books for the passed testator; 'advisor' books every
      // validated will the logged-in user holds, collected from the details
      // captured in this form.
      bookingMode: {
        type: String,
        default: 'testator',
      },
    },
    data() {
      return {
        icons: {
          mdiInformation,
        },
        showDryRunPreview: false,
        dryRunRequests: '',
        // Duplicate-pickup guard (advisers only, see duplicateCheckEnabled):
        // the pending booking found at the entered address, the address key it
        // was found for, and whether the modal is open.
        duplicateBooking: null,
        duplicateBookingAddressKey: '',
        showDuplicateDialog: false,
        duplicateCheckTimer: null,
        // Adviser bookings only: the wills the adviser holds that can be
        // collected, and which of them go with this booking.
        eligibleTestators: [],
        selectedTestatorIds: [],
        form: {
          email: '',
          fullName: '',
          cellNumber: '',
          cellCountryCode: 'ZA',
          alternativeCellNumber: '',
          altCellCountryCode: 'ZA',
          companyName: '',
          specialInstructionsCollection: '',
          streetNumber: '',
          streetName: '',
          suburb: '',
          city: '',
          province: '',
          postalCode: '',
          collectionDate: '',
          termsAndConditions: null,
          lat: null,
          lng: null,
        },
        provinces: [
          { text: 'Eastern Cape', value: 'Eastern Cape' },
          { text: 'Free State', value: 'Free State' },
          { text: 'Gauteng', value: 'Gauteng' },
          { text: 'KwaZulu-Natal', value: 'KwaZulu-Natal' },
          { text: 'Limpopo', value: 'Limpopo' },
          { text: 'Mpumalanga', value: 'Mpumalanga' },
          { text: 'Northern Cape', value: 'Northern Cape' },
          { text: 'North West', value: 'North West' },
          { text: 'Western Cape', value: 'Western Cape' },
        ],
        dateMenu: false,
        countryDialCode: '+27',
        altCountryDialCode: '+27',
        loading: false,
        showError: false,
        errorMessage: '',
        addressDetails: {
          streetNumber: '',
          streetName: '',
          suburb: '',
          city: '',
          province: '',
          postalCode: '',
          lat: null,
          lng: null,
        },
        showOtherAddressInputs: false,
        showMapPreview: false,
        map: null,
        marker: null,
        skipNextGeocode: false,
        geocodeTimer: null,
      };
    },
    created() {
      this.form.email = this.testator.email;
      this.form.fullName = this.testator.fullName;
      this.form.cellNumber = this.testator.cellNumber;
      this.form.cellCountryCode = this.testator.cellCountryCode;
      this.form.alternativeCellNumber = this.testator.alternativeCellNumber;
      this.form.altCellCountryCode = this.testator.altCellCountryCode || 'ZA';
    },
    computed: {
      minCollectionDate() {
        const today = new Date();
        today.setDate(today.getDate() + 2);
        return today.toISOString().slice(0, 10);
      },
      alternativeCellNumberSignificant() {
        if (!this.form.alternativeCellNumber) {
          return null;
        }
        const pn = parsePhoneNumber(this.form.alternativeCellNumber, {
          regionCode: this.form.altCellCountryCode,
        });
        return pn.valid
          ? pn.number.significant
          : this.form.alternativeCellNumber;
      },
      addressQueryForGeocode() {
        return [
          this.form.streetNumber,
          this.form.streetName,
          this.form.suburb,
          this.form.city,
          this.form.province,
        ]
          .filter(Boolean)
          .join(', ');
      },
      hasEnoughAddressData() {
        return !!(
          this.form.city ||
          this.form.suburb ||
          (this.form.streetName && this.form.province)
        );
      },
      // Only advisers booking on someone else's behalf are stopped from
      // booking a second pickup at the same address: the dashboard's bulk
      // booking, or an adviser impersonating a testator. A testator booking
      // their own collection is never checked.
      duplicateCheckEnabled() {
        return (
          this.bookingMode === 'advisor' ||
          !!this.$store.getters.isLoggedInAsSomeoneElse
        );
      },
      // The fields the backend matches on, normalised the same way it does,
      // plus the map pin: the backend also treats "same street number, pin on
      // the same spot" as the same address, so a settled or dragged pin must
      // re-run the lookup.
      addressKey() {
        const text = [
          this.form.streetNumber,
          this.form.streetName,
          this.form.postalCode,
        ].map(part => (part || '').trim().replace(/\s+/g, ' ').toLowerCase());
        const pin = [this.form.lat, this.form.lng].map(coord =>
          coord === null || coord === undefined ? '' : Number(coord).toFixed(5)
        );
        return [...text, ...pin].join('|');
      },
      hasAddressToCheck() {
        return !!(this.form.streetNumber && this.form.streetName);
      },
      duplicateBookingAtCurrentAddress() {
        return (
          this.duplicateCheckEnabled &&
          !!this.duplicateBooking &&
          this.duplicateBookingAddressKey === this.addressKey
        );
      },
      duplicateBookingWhen() {
        if (!this.duplicateBooking) return '';
        return (
          this.duplicateBooking.collectionDetails ||
          this.duplicateBooking.collectionDate ||
          ''
        );
      },
    },
    watch: {
      addressKey() {
        if (!this.duplicateCheckEnabled) return;

        clearTimeout(this.duplicateCheckTimer);
        if (!this.hasAddressToCheck) return;

        // Debounced so typing an address by hand does not fire a lookup per
        // keystroke; a picked Google address settles within one tick.
        this.duplicateCheckTimer = setTimeout(() => {
          this.checkForDuplicateBooking({ notify: true });
        }, 600);
      },
      addressQueryForGeocode() {
        if (this.skipNextGeocode) {
          return;
        }

        clearTimeout(this.geocodeTimer);

        if (this.hasEnoughAddressData) {
          this.geocodeTimer = setTimeout(() => {
            this.geocodeAndUpdateMap();
          }, 800);
        } else {
          this.showMapPreview = false;
        }
      },
    },
    beforeDestroy() {
      clearTimeout(this.geocodeTimer);
      clearTimeout(this.duplicateCheckTimer);
      if (this.marker) {
        google.maps.event.clearInstanceListeners(this.marker);
      }
      this.map = null;
      this.marker = null;
    },
    methods: {
      countrySelected(val) {
        this.form.cellCountryCode = val.iso2;
        this.countryDialCode = '+' + val.dialCode.toString();
      },

      altCountrySelected(val) {
        this.form.altCellCountryCode = val.iso2;
        this.altCountryDialCode = '+' + val.dialCode.toString();
      },

      onAddressSelected(addressDetails) {
        this.skipNextGeocode = true;
        this.form.streetNumber = addressDetails.streetNumber;
        this.form.streetName = addressDetails.streetName;
        this.form.suburb = addressDetails.suburb;
        this.form.city = addressDetails.city;
        this.form.province = addressDetails.province;
        this.form.postalCode = addressDetails.postalCode;
        this.form.lat = addressDetails.lat;
        this.form.lng = addressDetails.lng;

        this.addressDetails = { ...addressDetails };

        this.updateMapWithCoordinates(addressDetails.lat, addressDetails.lng);

        setTimeout(() => {
          this.skipNextGeocode = false;
        }, 100);
      },

      updateMapWithCoordinates(lat, lng) {
        if (!lat || !lng) {
          this.showMapPreview = false;
          return;
        }

        const position = {
          lat: parseFloat(lat),
          lng: parseFloat(lng),
        };
        this.showMapPreview = true;

        this.$nextTick(() => {
          if (!this.map) {
            this.initMap(position);
          } else {
            this.map.setCenter(position);
            this.map.setZoom(15);
            this.marker.setPosition(position);
            setTimeout(() => {
              if (this.map) {
                google.maps.event.trigger(this.map, 'resize');
                this.map.setCenter(position);
              }
            }, 550);
          }
        });
      },

      initMap(position) {
        if (!this.$refs.mapElement || !window.google || !window.google.maps) {
          return;
        }

        this.map = new google.maps.Map(this.$refs.mapElement, {
          center: position,
          zoom: 15,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });

        this.marker = new google.maps.Marker({
          position,
          map: this.map,
          draggable: true,
          animation: google.maps.Animation.DROP,
        });

        this.marker.addListener('dragend', event => {
          const newLat = event.latLng.lat();
          const newLng = event.latLng.lng();
          this.form.lat = newLat;
          this.form.lng = newLng;
        });

        setTimeout(() => {
          if (this.map) {
            google.maps.event.trigger(this.map, 'resize');
            this.map.setCenter(position);
          }
        }, 550);
      },

      parseGeocodeResultAddressComponents(placeResult) {
        const components = placeResult?.address_components || [];
        const parsed = {
          streetNumber: '',
          streetName: '',
          suburb: '',
          city: '',
          province: '',
          postalCode: '',
        };
        for (const component of components) {
          const types = component.types;
          if (types.includes('street_number'))
            parsed.streetNumber = component.long_name;
          if (types.includes('route')) parsed.streetName = component.long_name;
          if (types.includes('sublocality') || types.includes('neighborhood'))
            parsed.suburb = component.long_name;
          if (types.includes('locality')) parsed.city = component.long_name;
          if (types.includes('administrative_area_level_1'))
            parsed.province = component.long_name;
          if (types.includes('postal_code'))
            parsed.postalCode = component.long_name;
        }
        return parsed;
      },

      applyGeocodeToFormAndAddress(parsed) {
        const fields = [
          'streetNumber',
          'streetName',
          'suburb',
          'city',
          'province',
          'postalCode',
        ];
        for (const field of fields) {
          const value = parsed[field];
          if (value && !this.form[field]) {
            this.form[field] = value;
          }
        }
        this.addressDetails = {
          ...this.addressDetails,
          streetNumber: this.form.streetNumber,
          streetName: this.form.streetName,
          suburb: this.form.suburb,
          city: this.form.city,
          province: this.form.province,
          postalCode: this.form.postalCode,
          lat: this.form.lat,
          lng: this.form.lng,
        };
      },

      geocodeAndUpdateMap() {
        if (!window.google || !window.google.maps) {
          return;
        }

        const geocoder = new google.maps.Geocoder();
        const address = this.addressQueryForGeocode + ', South Africa';

        geocoder.geocode({ address }, (results, status) => {
          if (status === 'OK' && results[0]) {
            const result = results[0];
            const location = result.geometry.location;
            const newLat = location.lat();
            const newLng = location.lng();

            this.form.lat = newLat;
            this.form.lng = newLng;

            const parsed = this.parseGeocodeResultAddressComponents(result);
            this.applyGeocodeToFormAndAddress(parsed);
            this.updateMapWithCoordinates(newLat, newLng);
          }
        });
      },

      // Asks the backend whether a pickup is already booked, and not yet
      // collected, at the address currently in the form. Returns that booking
      // (or null) and remembers it for the inline warning. With notify, a
      // match opens the modal. A failed lookup never blocks the form - the
      // mutation re-checks server-side regardless.
      async checkForDuplicateBooking({ notify = false } = {}) {
        if (!this.duplicateCheckEnabled || !this.hasAddressToCheck) {
          return null;
        }

        const addressKey = this.addressKey;
        try {
          const response = await this.$apollo.query({
            query: PENDING_WILL_COLLECTION_BOOKING_AT_ADDRESS_QUERY,
            variables: {
              streetNumber: this.form.streetNumber,
              streetName: this.form.streetName,
              postalCode: this.form.postalCode || null,
              lat: this.form.lat === null ? null : Number(this.form.lat),
              lng: this.form.lng === null ? null : Number(this.form.lng),
            },
            fetchPolicy: 'network-only',
          });

          // The address changed while the lookup was in flight; the newer
          // lookup (or the submit-time one) will report on the current one.
          if (addressKey !== this.addressKey) return null;

          const booking =
            response?.data?.pendingWillCollectionBookingAtAddress || null;
          this.rememberDuplicateBooking(booking, addressKey);

          if (booking && notify) {
            this.presentExistingCollection(booking);
          }
          return booking;
        } catch (error) {
          console.warn(
            'Could not check for an existing collection booking:',
            error
          );
          return null;
        }
      },

      rememberDuplicateBooking(booking, addressKey) {
        this.duplicateBooking = booking;
        this.duplicateBookingAddressKey = booking ? addressKey : '';
      },

      // Opens the "add to the existing collection" dialog, or hands it to a
      // dialog host that will show it once this form's dialog has closed.
      // The host gets the wills the adviser had ticked so the dialog can
      // start with the same selection.
      presentExistingCollection(booking) {
        if (this.showExistingCollectionInline) {
          this.showDuplicateDialog = true;
        } else {
          this.$emit('existing-collection', {
            booking,
            testatorIds: [...this.selectedTestatorIds],
          });
        }
      },

      isTestatorSelected(id) {
        return this.selectedTestatorIds.includes(String(id));
      },
      toggleTestator(id, checked) {
        const key = String(id);
        if (checked && !this.selectedTestatorIds.includes(key)) {
          this.selectedTestatorIds = [...this.selectedTestatorIds, key];
        } else if (!checked) {
          this.selectedTestatorIds = this.selectedTestatorIds.filter(
            s => s !== key
          );
        }
      },

      // So the overview re-reads the testator (and now sees the booking)
      // instead of serving the cached, pre-booking copy.
      evictTestatorFromCache() {
        try {
          if (
            this.$apollo &&
            this.$apollo.client &&
            this.$apollo.client.cache
          ) {
            this.$apollo.client.cache.evict({
              fieldName: 'testator',
              args: { id: this.testator.id },
            });
            this.$apollo.client.cache.gc();
          }
        } catch (error) {
          console.warn('Failed to clear Apollo cache:', error);
        }
      },

      // Wills were added to the collection already booked at this address.
      // Hosts treat it exactly like a booking: dashboard rows flip to "ready
      // to collect", the overview route returns home.
      onLinkedToExistingBooking({ linkedTestatorIds, errors }) {
        if (this.bookingMode !== 'advisor') this.evictTestatorFromCache();
        if (errors && errors.length) {
          this.errorMessage = errors.join(', ');
          this.showError = true;
        }
        this.$emit('booking-success', {
          dryRun: false,
          linked: true,
          bookedTestatorIds: linkedTestatorIds,
        });
      },

      async submitForm() {
        this.$refs.observer.validate().then(async valid => {
          if (valid) {
            if (!this.form.lat || !this.form.lng) {
              this.errorMessage =
                'Unable to determine your location coordinates. Please use the address search or ensure your address details are complete, and confirm the pin is placed on the map.';
              this.showError = true;
              return;
            }
            if (
              this.bookingMode === 'advisor' &&
              !this.selectedTestatorIds.length
            ) {
              this.errorMessage = this.eligibleTestators.length
                ? 'Select at least one will you have in your possession.'
                : 'None of the wills you hold are ready for collection.';
              this.showError = true;
              return;
            }
            this.loading = true;
            try {
              // Fresh lookup at submit time: the debounced one may be stale or
              // still pending, and the adviser may have dismissed the modal.
              const duplicate = await this.checkForDuplicateBooking();
              if (duplicate) {
                this.presentExistingCollection(duplicate);
                return;
              }

              const { termsAndConditions, ...bookingPayload } = this.form;

              if (
                bookingPayload.cellNumber &&
                !bookingPayload.cellNumber.startsWith('+')
              ) {
                bookingPayload.cellNumber =
                  this.countryDialCode + bookingPayload.cellNumber;
              }

              if (
                this.alternativeCellNumberSignificant &&
                !this.form.alternativeCellNumber.startsWith('+')
              ) {
                bookingPayload.alternativeCellNumber =
                  this.altCountryDialCode +
                  this.alternativeCellNumberSignificant;
              }

              const isAdvisorBooking = this.bookingMode === 'advisor';
              const response = await this.$apollo.mutate({
                mutation: isAdvisorBooking
                  ? CREATE_ADVISOR_WILL_COLLECTION_BOOKINGS_MUTATION
                  : CREATE_TESTATOR_WILL_COLLECTION_BOOKING_MUTATION,
                variables: isAdvisorBooking
                  ? {
                      willCollectionBookingInput: bookingPayload,
                      testatorIds: this.selectedTestatorIds,
                    }
                  : {
                      testatorId: this.testator.id,
                      willCollectionBookingInput: bookingPayload,
                    },
              });
              const payload =
                response &&
                response.data &&
                (isAdvisorBooking
                  ? response.data.createAdvisorWillCollectionBookings
                  : response.data.createTestatorWillCollectionBooking);

              if (payload && !payload.errors.length) {
                if (!isAdvisorBooking) this.evictTestatorFromCache();

                const dryRunRequests = payload.dryRunRequests;
                const successResult = {
                  dryRun: !!dryRunRequests,
                  bookedTestatorIds: payload.bookedTestatorIds || null,
                };

                if (dryRunRequests) {
                  if (!this.showPreviewInline) {
                    this.$emit('booking-success', {
                      ...successResult,
                      dryRunRequests,
                    });
                    return;
                  }
                  this.dryRunRequests = dryRunRequests;
                  this.showDryRunPreview = true;
                  return;
                }

                this.$emit('booking-success', successResult);
              } else if (payload && payload.duplicateAddressBooking) {
                // Server-side backstop for the same rule: nothing was booked.
                this.rememberDuplicateBooking(
                  payload.duplicateAddressBooking,
                  this.addressKey
                );
                this.presentExistingCollection(payload.duplicateAddressBooking);
              } else {
                this.errorMessage =
                  (payload && payload.errors && payload.errors.join(', ')) ||
                  'An error occurred while booking.';
                this.showError = true;
              }
            } catch (e) {
              this.errorMessage =
                e.message || 'An error occurred while booking.';
              this.showError = true;
            } finally {
              this.loading = false;
            }
          } else {
            const errors = this.$refs.observer.ctx.errors;
            document
              .getElementById(
                Object.keys(errors).find(field => errors[field].length)
              )
              .scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        });
      },
      onDryRunPreviewInput(open) {
        if (!open) {
          this.$emit('booking-success', { dryRun: true });
        }
      },
      closeDialog() {
        this.$emit('close');
      },
    },
    apollo: {
      eligibleTestators: {
        query: ADVISOR_COLLECTION_ELIGIBLE_TESTATORS_QUERY,
        update: data => data.advisorCollectionEligibleTestators || [],
        skip() {
          return this.bookingMode !== 'advisor';
        },
        fetchPolicy: 'network-only',
        result({ data }) {
          // Nothing is ticked for the adviser; only drop a tick whose will
          // is no longer eligible after a refetch.
          const list = (data && data.advisorCollectionEligibleTestators) || [];
          const ids = list.map(t => String(t.id));
          this.selectedTestatorIds = this.selectedTestatorIds.filter(id =>
            ids.includes(id)
          );
        },
        error(error) {
          console.error('Error fetching wills ready for collection:', error);
        },
      },
    },
  };
</script>

<style lang="scss">
  .will-collection-booking-form-container {
    input[name='telephone'] {
      display: none;
    }
  }
</style>

<style scoped lang="scss">
  .v-card {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
    border-radius: 8px;
  }

  .testator-picker {
    max-height: 240px;
    overflow-y: auto;
  }

  .text-danger {
    color: #910822;
  }

  .booking-form-header {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  .terms-link {
    text-decoration: none;
    cursor: pointer;

    &:visited {
      text-decoration: none;
    }

    &:hover {
      text-decoration: none;
    }
  }
  .vue-tel-input {
    padding: 10px !important;
  }

  .map-wrapper {
    max-height: 0;
    opacity: 0;
    overflow: hidden;
    transition: max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s ease,
      margin 0.5s cubic-bezier(0.4, 0, 0.2, 1),
      padding 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    margin-bottom: 0;
    padding: 0;

    &.map-visible {
      max-height: 320px;
      opacity: 1;
      margin-bottom: 12px;
      padding-top: 4px;
    }
  }

  .map-element {
    height: 300px;
    width: 100%;
    border-radius: 8px;
  }
</style>
